import Contact from 'pages/contact';
import { FC, useEffect, useState } from 'react';
// Redux
import { Provider } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import rootReducer from 'store/reducers';
import styled from 'styled-components';
// Pages imports
import useSWR, { SWRConfig } from 'swr';
import User from 'types/User';
import WebSocketManager from 'utils/websocket-manager';
import LoadingScreen from './components/LoadingScreen';
import NavigationRoutes from './components/navigation/NavigationRoutes';
import './css/App.css';
import './fonts/fonts.css';
import HomePage from './pages';
import AdminRoutes from './pages/admin/Routes';
import AuthenticationRoutes from './pages/authentication/Routes';
import MenteeRoutes from './pages/mentee/Routes';
import MentorRoutes from './pages/mentor/Routes';
import UserRoutes from './pages/user/Routes';
import privateAPI from './utils/privateAxiosInstance';

type SharedStylesProps = {
  tabOutline: boolean;
} & React.HTMLProps<HTMLDivElement>;
/* eslint-disable @typescript-eslint/no-unused-vars */
const SharedStylesDiv: FC<SharedStylesProps> = ({ tabOutline, ...props }) => {
  return <div {...props} />;
};
const SharedStyles = styled(SharedStylesDiv)`
  * {
    --outline: ${({ tabOutline }) =>
      tabOutline ? 'medium solid black' : 'none'};
  }
`;

const store = createStore(
  rootReducer,
  composeWithDevTools(applyMiddleware(thunk)),
);

function App() {
  const { data: user } = useSWR<User>('/auth/profile');

  useEffect(() => {
    if (user) {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) WebSocketManager.init(accessToken, user);
    }
  }, [user]);

  const [tabOutline, setTabOutline] = useState<boolean>(false);
  useEffect(() => {
    function handleFirstTab(e: KeyboardEvent) {
      if (e.code === 'Tab') {
        console.log(e);
        setTabOutline(true);

        window.removeEventListener('keydown', handleFirstTab);
        window.addEventListener('mousedown', handleMouseDownOnce);
      }
    }

    function handleMouseDownOnce() {
      setTabOutline(false);

      window.removeEventListener('mousedown', handleMouseDownOnce);
      window.addEventListener('keydown', handleFirstTab);
    }

    window.addEventListener('keydown', handleFirstTab);
  }, []);

  return (
    <SharedStyles
      {...{
        tabOutline,
      }}
    >
      <div className="App">
        <Provider store={store}>
          <SWRConfig
            value={{
              refreshInterval: 3000,
              fetcher: (url) => privateAPI.get(url).then((res) => res.data),
              //AJOUT DU 05/05/2026 : Désactiver le retry SWR sur les erreurs 401 (cette modification stoppe définitivement le flood de requêtes en production)
              onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
                // Ne pas réessayer sur les erreurs 401
                if (error?.response?.status === 401) return;
                // Ne pas réessayer plus de 3 fois
                if (retryCount >= 3) return;
                // Réessayer après 5 secondes
                setTimeout(() => revalidate({ retryCount }), 5000);
              },
            }}
          >
            <LoadingScreen />
            <NavigationRoutes
              routesWithNavigation={[
                '/contact',
                '/admin/*',
                '/mentor/mentorship/*',
                '/mentee/mentorship/*',
                '/settings/*',
                '/',
              ]}
            />
            <section id="page">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin/*" element={<AdminRoutes />} />
                <Route path="/mentee/*" element={<MenteeRoutes />} />
                <Route path="/mentor/*" element={<MentorRoutes />} />
                <Route
                  path="/authentication/*"
                  element={<AuthenticationRoutes />}
                />
                <Route path="/*" element={<UserRoutes />} />
              </Routes>
            </section>
          </SWRConfig>
        </Provider>
      </div>
    </SharedStyles>
  );
}

export default App;
